import { getSupportedProcessesInfo } from '../transactions/transaction';

const CURRENT_USER_BIDS_PAGE_SIZE = 100;
const CURRENT_USER_BIDS_FALLBACK_MAX_PAGES = 5;
const PROVIDER_BID_TRANSITIONS = [
  'transition/make-offer',
  'transition/make-offer-after-inquiry',
  'transition/make-offer-from-request',
  'transition/provider-make-counter-offer',
];

export const uuidToString = id => id?.uuid || (id ? `${id}` : null);

const emptyBidStatusByListingId = listingIds =>
  listingIds.reduce((statuses, id) => {
    statuses[id] = false;
    return statuses;
  }, {});

const transactionListingId = tx =>
  tx?.relationships?.listing?.data?.id?.uuid || tx?.listing?.id?.uuid;

const transactionHasCurrentProviderBid = tx => {
  const metadataOffers = tx?.attributes?.metadata?.offers;
  const hasProviderOfferInMetadata =
    Array.isArray(metadataOffers) &&
    metadataOffers.some(
      offer => offer?.by === 'provider' && PROVIDER_BID_TRANSITIONS.includes(offer?.transition)
    );

  const transitions = tx?.attributes?.transitions;
  const hasProviderOfferTransition =
    Array.isArray(transitions) &&
    transitions.some(
      transition =>
        transition?.by === 'provider' && PROVIDER_BID_TRANSITIONS.includes(transition?.transition)
    );

  const lastTransition = tx?.attributes?.lastTransition;
  const hasProviderOfferLastTransition = PROVIDER_BID_TRANSITIONS.includes(lastTransition);

  return hasProviderOfferInMetadata || hasProviderOfferTransition || hasProviderOfferLastTransition;
};

const bidStatusByListingIdFromTransactions = (transactions, listingIds) => {
  const listingIdSet = new Set(listingIds);
  return (transactions || []).reduce((statuses, tx) => {
    const listingId = transactionListingId(tx);
    return listingId && listingIdSet.has(listingId) && transactionHasCurrentProviderBid(tx)
      ? { ...statuses, [listingId]: true }
      : statuses;
  }, emptyBidStatusByListingId(listingIds));
};

const mergeBidStatusMaps = (listingIds, firstMap, secondMap) =>
  listingIds.reduce((statuses, id) => {
    statuses[id] = firstMap?.[id] === true || secondMap?.[id] === true;
    return statuses;
  }, {});

const bidStatusByListingIdFromResponse = (response, listingIds) => {
  return bidStatusByListingIdFromTransactions(response?.data?.data, listingIds);
};

const providerBidTransactionQueryParams = params => {
  const processNames = getSupportedProcessesInfo().map(processInfo => processInfo.name);
  return {
    only: 'sale',
    processNames,
    include: ['listing'],
    'fields.transaction': ['lastTransition', 'lastTransitionedAt', 'metadata', 'transitions'],
    'fields.listing': ['title'],
    page: 1,
    perPage: CURRENT_USER_BIDS_PAGE_SIZE,
    ...params,
  };
};

const fetchProviderBidStatusesWithoutListingFilter = (sdk, listingIds) => {
  const fetchPage = (page, statuses) => {
    const params = providerBidTransactionQueryParams({ page });
    return sdk.transactions.query(params).then(response => {
      const pageStatuses = bidStatusByListingIdFromResponse(response, listingIds);
      const nextStatuses = mergeBidStatusMaps(listingIds, statuses, pageStatuses);
      const matchedAllListings = listingIds.every(id => nextStatuses[id] === true);
      const transactionCount = response?.data?.data?.length || 0;
      const totalPages = response?.data?.meta?.totalPages;
      const hasMorePages =
        totalPages != null ? page < totalPages : transactionCount === CURRENT_USER_BIDS_PAGE_SIZE;
      const shouldFetchNextPage =
        !matchedAllListings && hasMorePages && page < CURRENT_USER_BIDS_FALLBACK_MAX_PAGES;

      return shouldFetchNextPage ? fetchPage(page + 1, nextStatuses) : nextStatuses;
    });
  };

  return fetchPage(1, emptyBidStatusByListingId(listingIds));
};

export const fetchProviderBidStatuses = (sdk, listingIds) => {
  const normalizedListingIds = (listingIds || []).map(uuidToString).filter(Boolean);

  if (!sdk?.transactions?.query || normalizedListingIds.length === 0) {
    return Promise.resolve(emptyBidStatusByListingId(normalizedListingIds));
  }

  const params = providerBidTransactionQueryParams({ listingId: normalizedListingIds });
  return sdk.transactions
    .query(params)
    .then(response => bidStatusByListingIdFromResponse(response, normalizedListingIds))
    .catch(() => fetchProviderBidStatusesWithoutListingFilter(sdk, normalizedListingIds))
    .catch(() => emptyBidStatusByListingId(normalizedListingIds));
};

export const fetchProviderBidStatusForListing = (sdk, listingId) => {
  const listingIdString = uuidToString(listingId);
  return fetchProviderBidStatuses(sdk, [listingIdString]).then(
    bidStatusByListingId => bidStatusByListingId?.[listingIdString] === true
  );
};
